import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Interactive product icon orbit — stepped 7-slot elliptical carousel.
// Slots (clockwise from front): front center → front right → rear right →
// upper far-rear right-center → upper far-rear left-center → rear left →
// front left. The upper pair hugs the top center so the rear arc never
// opens a gap. Dragging follows lightly; on release the group settles
// exactly one orbit step with a calm 620ms cubic-bezier. All 7 icons stay
// mounted and visible at all times — nothing is teleported, swapped, or
// hidden. Transforms only.

const ITEMS = [
  { id: "playvia", name: "Playvia", logo: "/icons/playvia.png" },
  { id: "mindbrief", name: "MindBrief", logo: "/icons/mindbrief.png" },
  { id: "readywork", name: "ReadyWork", logo: "/icons/readywork.png" },
  { id: "skripsinext", name: "SkripsiNext", logo: "/icons/skripsinext.png" },
  { id: "profitpop", name: "ProfitPop", logo: "/icons/profitpop.png" },
  { id: "uptoday", name: "UpToday", logo: "/icons/uptoday.png" },
  { id: "sebelumsah", name: "SebelumSah", logo: "/icons/sebelumsah.png" },
];

const N = ITEMS.length; // 7
const mod = (a, n) => ((a % n) + n) % n;

// Fixed slot geometry (desktop px, before the mobile shrink factor).
// Front sits low and large; rear icons rise and soften toward the top.
const SLOTS = [
  { x: 0, y: 18, s: 1, o: 1, z: 70 }, // front center
  { x: 190, y: -6, s: 0.82, o: 0.95, z: 60 }, // front right
  { x: 212, y: -80, s: 0.65, o: 0.85, z: 40 }, // rear right
  { x: 80, y: -134, s: 0.54, o: 0.68, z: 10 }, // upper far-rear right-center
  { x: -80, y: -134, s: 0.54, o: 0.68, z: 10 }, // upper far-rear left-center
  { x: -212, y: -80, s: 0.65, o: 0.85, z: 40 }, // rear left
  { x: -190, y: -6, s: 0.82, o: 0.95, z: 60 }, // front left
];

const TRANSITION =
  "transform 620ms cubic-bezier(0.33, 0.72, 0.26, 1), opacity 620ms cubic-bezier(0.33, 0.72, 0.26, 1), filter 620ms cubic-bezier(0.33, 0.72, 0.26, 1)";

const lerp = (a, b, t) => a + (b - a) * t;

export const IconOrbit = () => {
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const navigate = useNavigate();
  const [frontIdx, setFrontIdx] = useState(0); // Playvia starts at front
  const st = useRef({
    step: 0, // integer orbit step
    frac: 0, // live drag progress, -1..1
    dragging: false,
    startX: 0,
  });

  const paint = () => {
    const el = trackRef.current;
    if (!el) return;
    const { step, frac } = st.current;
    const f = el.clientWidth < 480 ? 0.56 : 1; // mobile shrink factor

    for (let i = 0; i < N; i++) {
      const node = itemRefs.current[i];
      if (!node) continue;
      const d = mod(i - step, N);
      let p = SLOTS[d];
      if (frac < 0) {
        // dragging left — travel toward the next slot counter-clockwise
        const q = SLOTS[mod(d - 1, N)];
        const t = -frac;
        p = {
          x: lerp(SLOTS[d].x, q.x, t),
          y: lerp(SLOTS[d].y, q.y, t),
          s: lerp(SLOTS[d].s, q.s, t),
          o: lerp(SLOTS[d].o, q.o, t),
          z: SLOTS[d].z,
        };
      } else if (frac > 0) {
        const q = SLOTS[mod(d + 1, N)];
        const t = frac;
        p = {
          x: lerp(SLOTS[d].x, q.x, t),
          y: lerp(SLOTS[d].y, q.y, t),
          s: lerp(SLOTS[d].s, q.s, t),
          o: lerp(SLOTS[d].o, q.o, t),
          z: SLOTS[d].z,
        };
      }
      const soften = ((1 - p.s) * 1.6).toFixed(2);
      const bright = (0.93 + 0.07 * p.s).toFixed(3);
      const shadowA = 0.07 + 0.15 * p.s;
      const shadowY = (8 + 20 * p.s).toFixed(0);
      const shadowB = (18 + 28 * p.s).toFixed(0);

      node.style.transform = `translate(-50%, -50%) translate3d(${(
        p.x * f
      ).toFixed(1)}px, ${(p.y * f).toFixed(1)}px, 0) scale(${p.s.toFixed(3)})`;
      node.style.zIndex = String(p.z);
      node.style.opacity = p.o.toFixed(3);
      node.style.filter = `blur(${soften}px) brightness(${bright}) drop-shadow(0 ${shadowY}px ${shadowB}px rgba(15, 23, 42, ${shadowA.toFixed(3)}))`;
    }
  };

  const setTransitions = (on) => {
    for (let i = 0; i < N; i++) {
      const node = itemRefs.current[i];
      if (node) node.style.transition = on ? TRANSITION : "none";
    }
  };

  useEffect(() => {
    paint();
    const onResize = () => paint();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerDown = (e) => {
    st.current.dragging = true;
    st.current.startX = e.clientX;
    st.current.frac = 0;
    setTransitions(false);
    trackRef.current?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!st.current.dragging) return;
    const per = 120; // px of drag for one full orbit step (threshold ≈ 42px)
    const f = (e.clientX - st.current.startX) / per;
    st.current.frac = Math.max(-1, Math.min(1, f));
    paint();
  };

  const openDemo = (id) => {
    if (id === "sebelumsah") {
      window.open("https://sebelumsah.com/", "_blank", "noopener,noreferrer");
    } else {
      navigate(`/demo/${id}`);
    }
  };

  const endDrag = (e) => {
    const s = st.current;
    if (!s.dragging) return;
    s.dragging = false;

    const moved = Math.abs(e.clientX - s.startX);
    if (moved < 8) {
      // tap — open the featured icon's demo, or nudge a side icon forward
      const hit = document
        .elementFromPoint(e.clientX, e.clientY)
        ?.closest('[data-testid^="orbit-icon-"]');
      if (hit) {
        const id = hit.getAttribute("data-testid").replace("orbit-icon-", "");
        const i = ITEMS.findIndex((it) => it.id === id);
        const d = mod(i - s.step, N);
        if (d === 0) {
          openDemo(id);
          s.frac = 0;
          return;
        }
        // tap a side icon to step it toward the front
        s.step = mod(s.step + (d <= 3 ? 1 : -1), N);
      }
      s.frac = 0;
      setTransitions(true);
      paint();
      setFrontIdx(mod(s.step, N));
      return;
    }

    // settle exactly one step when dragged far enough, else glide back
    if (s.frac < -0.35) s.step = mod(s.step + 1, N);
    else if (s.frac > 0.35) s.step = mod(s.step - 1, N);
    s.frac = 0;
    setTransitions(true);
    paint();
    setFrontIdx(mod(s.step, N));
  };

  return (
    <div
      ref={trackRef}
      data-testid="icon-orbit"
      role="region"
      aria-label="MisiDigi product ecosystem — drag to explore"
      className="absolute inset-0 cursor-grab touch-pan-y select-none active:cursor-grabbing"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {ITEMS.map((item, i) => (
        <div
          key={item.id}
          ref={(el) => (itemRefs.current[i] = el)}
          data-testid={`orbit-icon-${item.id}`}
          className="absolute left-1/2 top-1/2 h-[165px] w-[165px] will-change-transform sm:h-[224px] sm:w-[224px]"
        >
          <img
            src={item.logo}
            alt={item.name}
            draggable={false}
            className="h-full w-full object-contain"
          />
        </div>
      ))}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-8 flex flex-col items-center gap-0.5"
      >
        <div
          key={frontIdx}
          data-testid="orbit-front-name"
          className="flex flex-col items-center gap-0.5 [animation:orbit-caption-in_500ms_ease-out_both]"
        >
          <span className="text-[14px] font-semibold tracking-[0.01em] text-ink">
            {ITEMS[frontIdx].name}
          </span>
          <button
            type="button"
            data-testid="orbit-view-demo"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => openDemo(ITEMS[frontIdx].id)}
            className="pointer-events-auto text-[12px] font-medium text-steel transition-colors hover:text-ink"
          >
            View demo →
          </button>
        </div>
      </div>
      <span className="pointer-events-none absolute inset-x-0 bottom-2 hidden text-center font-mono text-[9px] uppercase tracking-[0.18em] text-platinum sm:block">
        Drag to explore
      </span>
    </div>
  );
};
